import { get, isEmpty, some } from "lodash-es";
import type { SnapshotRootResponse } from '../types/snapshotDataTypes';
import { resolveCosFileExtension } from '../../ChatPage/components/SubComp/_utils';

export const  parseStringifiedJSONFields = (input: any): any => {
  if (typeof input === "string") {
    try {
      const parsed = JSON.parse(input);
      // Only parse if result is object or array (not primitive like number/string/boolean)
      if (typeof parsed === "object" && parsed !== null) {
        return parseStringifiedJSONFields(parsed);
      }
      return parsed;
    } catch {
      return input; // return as is if not valid JSON
    }
  }

  if (Array.isArray(input)) {
    return input.map(item => parseStringifiedJSONFields(item));
  }

  if (typeof input === "object" && input !== null) {
    const output: Record<string, any> = {};
    Object.keys(input).forEach(key => {
      output[key] = parseStringifiedJSONFields(input[key]);
    });
    return output;
  }

  // Return primitive values as-is
  return input;
};

export function hasDocumentType(messages: SnapshotRootResponse[]) {
  if (isEmpty(messages)) return false;
  // 利用some的短路特性，找到第一个匹配项就停止遍历
  return some(messages, (message) => {
    // 获取answer数组，若不存在则返回空数组避免报错
    const answers = get(message, 'answer', []);
    // 检查answer数组中是否有type为document的项
    return some(answers, (answer) => get(answer, 'type') === 'document');
  });
}

/**
 * 判断快照数据中是否包含正文 <cos> 标签返回的 excel 文件（xlsx/xls）
 * 用于与后端 ai_excel_file 真正表格区分：cos 标签来源应识别为智能表格（SmartTable）只读预览
 * @param messages - 快照消息数组
 * @returns 是否包含 cos 标签 excel
 */
export function hasCosExcelType(messages: SnapshotRootResponse[]): boolean {
  if (isEmpty(messages)) return false;
  return some(messages, (message) => {
    const answers = get(message, 'answer', []);
    return some(answers, (answer) => {
      if (get(answer, 'type') !== 'text') return false;
      const textValue = get(answer, 'value', '');
      const match = typeof textValue === 'string' ? textValue.match(/<cos>([\s\S]*?)<\/cos>/) : null;
      if (!match) return false;
      try {
        const cosData = JSON.parse(match[1]);
        const cosUrl = get(cosData, 'cos_url') || get(cosData, 'url') || '';
        const fileName = get(cosData, 'name') || get(cosData, 'title') || '';
        // 与 SubComp 渲染器一致：优先文件名，其次 URL 路径（忽略 query/hash），避免签名参数干扰扩展名判断
        const fileExt = resolveCosFileExtension(cosUrl, fileName);
        return fileExt === 'xlsx' || fileExt === 'xls';
      } catch {
        return false;
      }
    });
  });
}